import React, { ChangeEvent, useContext, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import { useTracking } from "react-tracking";

import { Input } from "@airhelp/react";
import { Box, Button, Center, Heading, VStack } from "@chakra-ui/react";
import { HTTPError } from "ky";

import { handleErrorMessage, signupUser } from "api/user/signup";
import { PAGE_PATHS } from "config/routes";
import { SessionContext, TrackingContext } from "contexts";
import { ErrorBadge } from "elements";
import UserConsentsForm from "elements/UserConsentsForm";
import { ISignupSubmitData, IUser } from "models/User";
import { emailPattern, minPasswordLength } from "utils/validations";

const SignupView = ({ email = "" }) => {
  const { login } = useContext(SessionContext);
  const { setLoginData } = useContext(TrackingContext);
  const navigate = useNavigate();
  const [apiError, setApiError] = useState<string | null>();
  const [inProgress, setInProgress] = useState<boolean>(false);
  const { trackEvent } = useTracking();
  const { t } = useTranslation();

  const form = useForm<ISignupSubmitData>({ mode: "onChange" });
  const { handleSubmit, control, formState, watch } = form;
  const { isValid, errors } = formState;

  const passwordConfirmationErrMsg = t("errors.passwords_do_not_match") as string;

  const handleLoginLinkClick = () => {
    navigate(generatePath(PAGE_PATHS.LOGIN_PAGE));
  };

  const onSubmit = async (data: ISignupSubmitData) => {
    setApiError(null);
    setInProgress(true);

    const { email, password, passwordConfirmation, privacyConsent, marketingConsent } = data;
    const userData = {
      email,
      password,
      passwordConfirmation,
      privacyConsent,
      marketingConsent,
    };

    const [error, response] = await signupUser(userData);
    const session = error ? null : response.user;

    const errorResponse = error as HTTPError;

    if (!session) {
      const errorMessage = await handleErrorMessage(errorResponse, t);
      setApiError(errorMessage);
      setInProgress(false);
    } else {
      setLoginData({ method: "email", user: session as IUser });
      login(session as IUser);

      // TODO: pass properties of created account
      trackEvent({ name: "accountCreated" });
    }

    trackEvent({
      name: "GAEvent",
      successful: !error,
      eventCategory: "ahplus",
      eventAction: "clicked",
      eventLabel: "ahplus_dashboard_signup_CTA",
    });
  };

  return (
    <>
      <Heading variant="h4" mb={4} textAlign="left">
        {t("authentication.signup_form.finish_registration")}
      </Heading>

      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack spacing={4} mb={10}>
          <Controller
            render={({ field: { onChange, name } }) => (
              <Box w="100%" data-testid="email-form-control">
                <Input
                  isInvalid={!!errors.email}
                  errorMessage={errors.email?.message || ""}
                  placeholder="youremail@domain.com"
                  size="lg"
                  label={t("authentication.email_address")}
                  name={name}
                  type="email"
                  id="email"
                  onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
                  data-testid="email"
                />
              </Box>
            )}
            name="email"
            control={control}
            rules={{
              required: { value: true, message: t("errors.required") },
              pattern: { value: emailPattern, message: t("errors.email_invalid") },
            }}
            defaultValue={email}
          />

          <Controller
            render={({ field: { onChange, name } }) => (
              <Box w="100%" data-testid="password-form-control">
                <Input
                  isInvalid={!!errors.password}
                  errorMessage={errors.password?.message || ""}
                  placeholder="********"
                  size="lg"
                  label={t("authentication.password_form.set_password")}
                  name={name}
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
                  data-testid="password"
                />
              </Box>
            )}
            name="password"
            control={control}
            rules={{
              required: { value: true, message: t("errors.required") },
              minLength: {
                value: minPasswordLength,
                message: t("errors.too_short", { min: minPasswordLength, attribute: t("authentication.password") }),
              },
            }}
            defaultValue=""
          />

          <Controller
            render={({ field: { onChange, name } }) => (
              <Box w="100%" data-testid="password-confirmation-form-control">
                <Input
                  isInvalid={!!errors.passwordConfirmation}
                  errorMessage={errors.passwordConfirmation?.message || ""}
                  placeholder="********"
                  size="lg"
                  label={t("authentication.password_form.repeat_password")}
                  name={name}
                  type="password"
                  id="passwordConfirmation"
                  autoComplete="new-password"
                  onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
                  data-testid="password-confirmation"
                />
              </Box>
            )}
            name="passwordConfirmation"
            control={control}
            rules={{
              required: { value: true, message: t("errors.required") },
              validate: (value) => value === watch("password") || passwordConfirmationErrMsg,
            }}
            defaultValue=""
          />
          <UserConsentsForm form={form} requireTerms requireMarketing />
          {apiError && (
            <Box w="100%">
              <ErrorBadge>{apiError}</ErrorBadge>
            </Box>
          )}
        </VStack>

        <Button
          size="s"
          width="100%"
          onClick={handleSubmit(onSubmit)}
          isDisabled={!isValid || inProgress}
          data-testid="signup-finish"
        >
          {t("authentication.signup_form.finish")}
        </Button>

        <Center mt={2}>
          <Button variant="link" fontSize="sm" onClick={handleLoginLinkClick}>
            {t("authentication.i_already_have_account")}
          </Button>
        </Center>
      </form>
    </>
  );
};

export default SignupView;
